/**
 * MFA enrollment and disable flows (FEA-04), used on the Profile page.
 *
 * Enrollment: setup (server issues a pending secret) -> QR code / manual key ->
 * user confirms with a live TOTP code -> server enables MFA and returns
 * recovery codes EXACTLY ONCE - rendered with a strong "save these now"
 * warning and a copy button.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import * as authApi from '../api/auth';
import { useAuth } from '../lib/auth';
import { ErrorAlert, Field } from './ui';
import { cn, ui } from '../lib/ui';

export default function MfaSettings() {
  const { user, patchUser } = useAuth();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Enrollment state
  const [pending, setPending] = useState(null); // { secret, otpauthUri }
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  // Disable state
  const [disableCode, setDisableCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  useEffect(() => {
    if (!pending) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(pending.otpauthUri, { margin: 1, width: 220 })
      .then((url) => !cancelled && setQrDataUrl(url))
      .catch(() => !cancelled && setQrDataUrl(null));
    return () => {
      cancelled = true;
    };
  }, [pending]);

  async function startEnrollment() {
    setError(null);
    setBusy(true);
    try {
      const data = await authApi.mfaSetup();
      setPending({ secret: data.secret, otpauthUri: data.otpauthUri });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnrollment(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const data = await authApi.mfaVerifySetup(code);
      setRecoveryCodes(data.recoveryCodes || []);
      setPending(null);
      setCode('');
      patchUser({ mfaEnabled: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function disableMfa(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.mfaDisable(useRecovery ? { recoveryCode: disableCode } : { code: disableCode });
      setDisableCode('');
      setRecoveryCodes(null);
      patchUser({ mfaEnabled: false });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function copyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  // One-time recovery-codes screen, straight after enabling.
  if (recoveryCodes) {
    return (
      <section className={ui.card}>
        <h3 className={ui.h3}>Two-factor authentication enabled</h3>
        <div className={ui.alertInfo}>
          <strong>Save these recovery codes now — they will not be shown again.</strong>{' '}
          Each code signs you in once if you lose your authenticator.
        </div>
        <pre className="mb-4 overflow-x-auto rounded-dcon-sm bg-z-sunken p-4 font-mono text-data text-dcon-ink">
          {recoveryCodes.join('\n')}
        </pre>
        <div className={ui.row}>
          <button className={ui.btnSecondary} onClick={copyRecoveryCodes}>
            {copied ? 'Copied ✓' : 'Copy codes'}
          </button>
          <button className={ui.btnPrimary} onClick={() => setRecoveryCodes(null)}>
            I have saved them
          </button>
        </div>
      </section>
    );
  }

  if (user && user.mfaEnabled) {
    return (
      <section className={ui.card}>
        <h3 className={ui.h3}>Two-factor authentication</h3>
        <p>
          <span className={ui.pillSuccess}>enabled</span> Signing in requires a code from
          your authenticator app.
        </p>
        <ErrorAlert error={error} />
        <form onSubmit={disableMfa}>
          <Field
            label={useRecovery ? 'Recovery code' : 'Current authenticator code'}
            required
            autoComplete="one-time-code"
            hint="Required to turn off two-factor authentication."
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
          />
          <div className={ui.row}>
            <button className={ui.btnDanger} type="submit" disabled={busy}>
              {busy ? 'Disabling…' : 'Disable two-factor'}
            </button>
            <button
              type="button"
              className={ui.btnSecondary}
              onClick={() => setUseRecovery((v) => !v)}
            >
              {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
            </button>
          </div>
        </form>
      </section>
    );
  }

  if (pending) {
    return (
      <section className={ui.card}>
        <h3 className={ui.h3}>Set up two-factor authentication</h3>
        <p className="text-body-s">
          Scan the QR code with your authenticator app (Google Authenticator, 1Password,
          Authy…), or enter the key manually. Then confirm with the 6-digit code the app
          shows.
        </p>
        {qrDataUrl && (
          <p>
            <img src={qrDataUrl} alt="TOTP enrollment QR code" width="220" height="220" />
          </p>
        )}
        <p className="text-body-s">
          Manual entry key:{' '}
          <code className="font-mono text-data break-all select-all">{pending.secret}</code>
        </p>
        <ErrorAlert error={error} />
        <form onSubmit={confirmEnrollment}>
          <Field
            label="6-digit code from your app"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <div className={ui.row}>
            <button className={ui.btnPrimary} type="submit" disabled={busy}>
              {busy ? 'Confirming…' : 'Confirm and enable'}
            </button>
            <button type="button" className={ui.btnSecondary} onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section className={ui.card}>
      <h3 className={ui.h3}>Two-factor authentication</h3>
      <p className={cn(ui.muted, 'text-body-s')}>
        Add a second factor so a stolen password alone cannot access your account. You&rsquo;ll
        confirm each sign-in with a code from your authenticator app.
      </p>
      <ErrorAlert error={error} />
      <button className={ui.btnPrimary} onClick={startEnrollment} disabled={busy}>
        {busy ? 'Preparing…' : 'Enable two-factor'}
      </button>
    </section>
  );
}
